import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import posthog from 'posthog-js';
import ModalFrame from '../../../../ui/primitives/ModalFrame';
import { useDirectedPopup } from '../../../../ui/popupDirector/popupDirector';
import { useUserData } from '../../../../hooks/useUserData';
import type { UserData } from '../../../../models/user';
import { comebackDeclineUserComebackDeclinePost, comebackReplayUserComebackReplayPost } from '../../../../client';
import { format_num } from '../../../../utils/format/format_num';
import { ELDER_SRC } from '../../../room/tutorial/TutorialSpotlight';
import { ONBOARDING_REWARD_EXP, ONBOARDING_REWARD_PEARLS } from './onboardingRewardShared';

const SHELL_ICON = '/ui/shell-icon.png';
/** The VIP item's picture in the shop. */
const VIP_IMAGE = '/ui/item-icons/2.png';

/**
 * Back after a long absence (the server opens the offer, app/user/comeback.py):
 * one window — the VIP gift that came with the return (at most every two
 * months), and the offer to replay the tutorial for its reward again. The
 * interior tutorial runs again on the next visit to their house either way.
 */
export default function ComebackOfferHost() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: userData } = useUserData();
  const [busy, setBusy] = useState(false);
  const offer = !!userData?.comeback_offer;
  const vipDays = userData?.comeback_vip_days ?? 0;
  const visible = useDirectedPopup('comebackOffer', offer);

  useEffect(() => {
    if (!offer) return;
    // The return also reset the interior tutorial on the server.
    void queryClient.invalidateQueries({ queryKey: ['onboarding-state'] });
  }, [offer, queryClient]);

  useEffect(() => {
    if (visible) posthog.capture('comeback_offer_shown');
  }, [visible]);

  if (!visible) return null;

  const closeOffer = () => queryClient.setQueryData<UserData>(['userData'], (old) => (old ? { ...old, comeback_offer: false } : old));

  const decline = () => {
    posthog.capture('comeback_offer_declined');
    closeOffer();
    void comebackDeclineUserComebackDeclinePost().catch(() => { /* asked again next launch at worst */ });
  };

  const replay = async () => {
    setBusy(true);
    posthog.capture('comeback_offer_replay');
    try {
      await comebackReplayUserComebackReplayPost({ throwOnError: true });
      closeOffer();
      await queryClient.invalidateQueries({ queryKey: ['onboarding-state'] });
      void queryClient.invalidateQueries({ queryKey: ['userData'] });
      navigate('/onboarding-stories', { replace: true });
    } catch {
      closeOffer();
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalFrame isOpen onClose={decline} variant="dialog" hideHeader className="max-w-[360px] rounded-xl border border-white-alpha-10">
      <div className="flex flex-col items-center gap-md pt-md text-center [font-family:Lato,sans-serif] text-white">
        {vipDays > 0 ? (
          <img src={VIP_IMAGE} alt="VIP" className="h-[112px] w-[112px] object-contain drop-shadow-[0_6px_18px_var(--color-reward-alpha-40)]" />
        ) : (
          <img src={ELDER_SRC} alt="" className="h-[84px] w-[84px] rounded-full border-4 border-white bg-reward-highlight object-cover [object-position:50%_8%]" />
        )}
        <p className="m-0 [font-family:'Rubik_One',sans-serif] text-[20px] uppercase text-gold">{t("Welcome back!")}</p>
        {vipDays > 0 && (
          <p className="m-0 text-[15px] font-bold leading-[1.4]">
            {t("We missed you — here's VIP for {{days}} days, it's already on.", { days: vipDays })}
          </p>
        )}
        <div className="flex w-full flex-col items-center gap-sm border-t border-white-alpha-10 pt-md">
          <p className="m-0 text-[14px] leading-[1.4] text-white-alpha-70">
            {t("A lot has changed on the island. Replay the tutorial and get:")}
          </p>
          <span className="flex items-center gap-md [font-family:'Rubik_One',sans-serif] text-[17px] text-gold">
            +{format_num(ONBOARDING_REWARD_PEARLS)} <img src={SHELL_ICON} alt="" width={20} height={17} />
            +{format_num(ONBOARDING_REWARD_EXP)} XP
          </span>
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={() => void replay()}
          className="go-button mt-xs w-full cursor-pointer rounded-pill px-2xl py-md [font-family:'Rubik_One',sans-serif] text-[16px] text-white [text-shadow:0_1px_2px_var(--color-black-alpha-35)] disabled:opacity-60"
        >
          {t("Replay tutorial")}
        </button>
        <button type="button" onClick={decline} className="cursor-pointer border-none bg-transparent text-[14px] font-semibold text-white-alpha-50">
          {t("No, thanks")}
        </button>
      </div>
    </ModalFrame>
  );
}
